import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import { Comment } from '../../types.ts';
import ConfirmModal from '../common/ConfirmModal.tsx';
import {
  MessageSquare,
  CheckCircle,
  XCircle,
  Trash2,
  Search,
  Filter,
  User,
  Clock,
  ExternalLink,
  CornerDownRight,
  Send,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';

interface CommentsModerationManagerProps {
  primaryColor?: string;
}

export default function CommentsModerationManager({ primaryColor = '#059669' }: CommentsModerationManagerProps) {
  const { showToast, navigate } = useApp();

  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<'all' | 'approved' | 'pending' | 'spam'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [replyingCommentId, setReplyingCommentId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/comments');
      const data = await res.json();
      if (data.success) {
        setComments(data.data || []);
      }
    } catch (err) {
      console.error(err);
      showToast('Gagal memuat daftar komentar', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, []);

  const handleUpdateStatus = async (commentId: string, status: 'approved' | 'pending' | 'spam') => {
    try {
      const res = await fetch(`/api/comments/${commentId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (data.success) {
        showToast(data.message || `Status komentar diubah menjadi ${status}`, 'success');
        setComments((prev) => prev.map((c) => (c.id === commentId ? { ...c, status } : c)));
      }
    } catch (err) {
      console.error(err);
      showToast('Gagal mengubah status komentar', 'error');
    }
  };

  const [commentToDelete, setCommentToDelete] = useState<Comment | null>(null);

  const executeDeleteComment = async () => {
    if (!commentToDelete) return;
    const commentId = commentToDelete.id;

    try {
      const res = await fetch(`/api/comments/${commentId}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (data.success) {
        showToast('Komentar berhasil dihapus', 'success');
        setComments((prev) => prev.filter((c) => c.id !== commentId));
        setCommentToDelete(null);
      }
    } catch (err) {
      console.error(err);
      showToast('Gagal menghapus komentar', 'error');
      setCommentToDelete(null);
    }
  };

  const handleSendReply = async (parentComment: Comment) => {
    if (!replyText.trim()) {
      showToast('Mohon tulis balasan Anda', 'error');
      return;
    }

    setIsSubmittingReply(true);
    try {
      const res = await fetch('/api/comments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          articleId: parentComment.articleId,
          authorName: 'Admin Redaksi',
          authorEmail: 'admin@nusantara.com',
          content: `@${parentComment.authorName} ${replyText.trim()}`,
        }),
      });

      const data = await res.json();
      if (data.success && data.data) {
        showToast('Balasan Anda berhasil diterbitkan!', 'success');
        setComments([data.data, ...comments]);
        setReplyingCommentId(null);
        setReplyText('');
      }
    } catch (err) {
      console.error(err);
      showToast('Gagal mengirim balasan komentar', 'error');
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const filteredComments = comments.filter((c) => {
    const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch =
      !q ||
      c.authorName.toLowerCase().includes(q) ||
      c.content.toLowerCase().includes(q) ||
      (c.articleTitle && c.articleTitle.toLowerCase().includes(q));

    return matchesStatus && matchesSearch;
  });

  const pendingCount = comments.filter((c) => c.status === 'pending').length;
  const approvedCount = comments.filter((c) => c.status === 'approved').length;
  const spamCount = comments.filter((c) => c.status === 'spam').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-emerald-600" />
            <span>Moderasi Komentar & Tanggapan Pembaca</span>
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Kelola tanggapan pembaca pada artikel blog dan halaman produk. Menyetujui, membalas, atau menghapus spam.
          </p>
        </div>

        <button
          onClick={fetchComments}
          className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl flex items-center gap-1.5 self-start sm:self-auto"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          <span>Segarkan Data</span>
        </button>
      </div>

      {/* Stats and Filter Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <button
          onClick={() => setFilterStatus('all')}
          className={`p-3 rounded-xl border text-left transition-all ${
            filterStatus === 'all'
              ? 'bg-slate-900 text-white border-slate-900 shadow-xs'
              : 'bg-white border-slate-200 text-slate-800 hover:bg-slate-50'
          }`}
        >
          <span className="text-[10px] font-bold uppercase tracking-wider block opacity-75">Semua Komentar</span>
          <span className="text-xl font-extrabold">{comments.length}</span>
        </button>

        <button
          onClick={() => setFilterStatus('approved')}
          className={`p-3 rounded-xl border text-left transition-all ${
            filterStatus === 'approved'
              ? 'bg-emerald-700 text-white border-emerald-700 shadow-xs'
              : 'bg-white border-slate-200 text-emerald-900 hover:bg-emerald-50/50'
          }`}
        >
          <span className="text-[10px] font-bold uppercase tracking-wider block opacity-75">Disetujui (Public)</span>
          <span className="text-xl font-extrabold">{approvedCount}</span>
        </button>

        <button
          onClick={() => setFilterStatus('pending')}
          className={`p-3 rounded-xl border text-left transition-all ${
            filterStatus === 'pending'
              ? 'bg-amber-600 text-white border-amber-600 shadow-xs'
              : 'bg-white border-slate-200 text-amber-900 hover:bg-amber-50/50'
          }`}
        >
          <span className="text-[10px] font-bold uppercase tracking-wider block opacity-75">Menunggu Moderasi</span>
          <span className="text-xl font-extrabold">{pendingCount}</span>
        </button>

        <button
          onClick={() => setFilterStatus('spam')}
          className={`p-3 rounded-xl border text-left transition-all ${
            filterStatus === 'spam'
              ? 'bg-rose-700 text-white border-rose-700 shadow-xs'
              : 'bg-white border-slate-200 text-rose-900 hover:bg-rose-50/50'
          }`}
        >
          <span className="text-[10px] font-bold uppercase tracking-wider block opacity-75">Spam / Ditolak</span>
          <span className="text-xl font-extrabold">{spamCount}</span>
        </button>
      </div>

      {/* Search Input */}
      <div className="relative">
        <input
          type="text"
          placeholder="Cari komentar berdasarkan nama penulis, kata kunci, atau judul artikel..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 text-xs bg-white border border-slate-300 rounded-xl font-medium focus:ring-2 focus:ring-emerald-500 shadow-2xs"
        />
        <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
      </div>

      {/* Comment List */}
      {loading ? (
        <div className="py-12 text-center bg-white rounded-2xl border border-slate-200">
          <div className="w-8 h-8 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-xs font-semibold text-slate-500">Memuat komentar pembaca...</p>
        </div>
      ) : filteredComments.length === 0 ? (
        <div className="py-12 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-300">
          <MessageSquare className="w-8 h-8 text-slate-400 mx-auto mb-2" />
          <h4 className="font-bold text-sm text-slate-700">Tidak Ada Komentar Ditemukan</h4>
          <p className="text-xs text-slate-500 mt-1">
            {searchQuery || filterStatus !== 'all'
              ? 'Coba atur ulang kata kunci pencarian atau filter status komentar.'
              : 'Belum ada komentar dari pengunjung website.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredComments.map((comment) => (
            <div
              key={comment.id}
              className={`p-4 rounded-2xl border bg-white shadow-2xs transition-all ${
                comment.status === 'pending'
                  ? 'border-amber-300 bg-amber-50/30'
                  : comment.status === 'spam'
                  ? 'border-rose-200 bg-rose-50/30'
                  : 'border-slate-200'
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2 mb-2 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-slate-800 text-white font-bold text-xs flex items-center justify-center shrink-0">
                    {comment.authorName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-xs text-slate-900">{comment.authorName}</h4>
                      {comment.authorEmail && (
                        <span className="text-[11px] text-slate-400 font-mono">({comment.authorEmail})</span>
                      )}
                    </div>
                    <span className="text-[10px] text-slate-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(comment.createdAt).toLocaleString('id-ID', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                </div>

                {/* Status Pill & Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                      comment.status === 'approved'
                        ? 'bg-emerald-100 text-emerald-800'
                        : comment.status === 'pending'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-rose-100 text-rose-800'
                    }`}
                  >
                    {comment.status === 'approved'
                      ? 'Approved'
                      : comment.status === 'pending'
                      ? 'Menunggu Moderasi'
                      : 'Spam / Ditolak'}
                  </span>
                </div>
              </div>

              {/* Comment Content */}
              <p className="text-xs text-slate-800 leading-relaxed my-2 whitespace-pre-line bg-slate-50 p-3 rounded-xl border border-slate-100">
                {comment.content}
              </p>

              {/* Related Article Link & Moderator Control Bar */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2 mt-2">
                <button
                  onClick={() => navigate('article-detail', comment.articleId)}
                  className="text-[11px] font-bold text-emerald-700 hover:underline flex items-center gap-1 shrink-0"
                >
                  <ExternalLink className="w-3 h-3" />
                  <span>Lihat Artikel Asli</span>
                </button>

                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                  {comment.status !== 'approved' && (
                    <button
                      onClick={() => handleUpdateStatus(comment.id, 'approved')}
                      className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold flex items-center gap-1 shadow-2xs"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      <span>Setujui (Publish)</span>
                    </button>
                  )}

                  {comment.status !== 'spam' && (
                    <button
                      onClick={() => handleUpdateStatus(comment.id, 'spam')}
                      className="px-3 py-1 bg-amber-100 hover:bg-amber-200 text-amber-900 rounded-lg text-xs font-bold flex items-center gap-1"
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      <span>Tandai Spam</span>
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setReplyingCommentId(replyingCommentId === comment.id ? null : comment.id);
                      setReplyText('');
                    }}
                    className="px-3 py-1 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xs font-bold flex items-center gap-1"
                  >
                    <CornerDownRight className="w-3.5 h-3.5" />
                    <span>Balas</span>
                  </button>

                  <button
                    onClick={() => setCommentToDelete(comment)}
                    className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg cursor-pointer"
                    title="Hapus Komentar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Reply Form */}
              {replyingCommentId === comment.id && (
                <div className="mt-3 p-3 bg-slate-100 rounded-xl border border-slate-200 space-y-2">
                  <div className="flex items-center justify-between text-xs font-bold text-slate-800">
                    <span className="flex items-center gap-1 text-emerald-800">
                      <CornerDownRight className="w-3.5 h-3.5" />
                      <span>Tulis Balasan Admin Redaksi:</span>
                    </span>
                    <button
                      onClick={() => setReplyingCommentId(null)}
                      className="text-[10px] text-slate-500 hover:underline cursor-pointer"
                    >
                      Batal
                    </button>
                  </div>
                  <textarea
                    rows={2}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder={`Balas tanggapan ${comment.authorName}...`}
                    className="w-full px-3 py-2 text-xs bg-white border border-slate-300 rounded-lg font-medium"
                  />
                  <div className="flex justify-end">
                    <button
                      onClick={() => handleSendReply(comment)}
                      disabled={isSubmittingReply}
                      className="px-4 py-1.5 bg-slate-900 text-white rounded-lg text-xs font-bold flex items-center gap-1 shadow-2xs disabled:opacity-50 cursor-pointer"
                    >
                      <Send className="w-3 h-3" />
                      <span>{isSubmittingReply ? 'Kirim...' : 'Kirim Balasan'}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={Boolean(commentToDelete)}
        title="Hapus Komentar"
        message={`Apakah Anda yakin ingin menghapus komentar dari "${commentToDelete?.authorName || 'pembaca'}"?`}
        confirmLabel="Ya, Hapus Komentar"
        isDanger={true}
        onConfirm={executeDeleteComment}
        onCancel={() => setCommentToDelete(null)}
      />
    </div>
  );
}
